import { IPropsElement } from "./constants";

export interface ElementPosition {
    rowIndex: number
    position: number
}

export const isVerticalDividePreview = (
    form: IPropsElement[][], rowIndex: number | undefined, newRow: boolean, sourceIndex?: number
) => !newRow && rowIndex !== undefined && (form[rowIndex]?.some(element => element.index !== sourceIndex) ?? false);

export const isRedundantRowSlot = (form: IPropsElement[][], sourceRow: number | undefined, slot: number) => (
    sourceRow !== undefined && form[sourceRow]?.length === 1 && slot === sourceRow + 1
);

export const getInsertionSlot = (
    elements: { position: number, left: number, width: number }[],
    pointerX: number,
    endPosition: number
) => elements.find(element => pointerX < element.left + element.width / 2)?.position ?? endPosition;

export const captureDragOrigin = (element: Element | undefined) => (
    element?.parentNode ? { element, parent: element.parentNode, nextSibling: element.nextSibling } : undefined
);

export const restoreDragOrigin = (origin: ReturnType<typeof captureDragOrigin>) => {
    if (!origin || !origin.parent.isConnected) return;
    const { element, parent, nextSibling } = origin;
    // Optimistic sorting moves DOM nodes. Restore React's original layout before
    // committing a different row structure, so React can safely reconcile it.
    const anchor = nextSibling?.parentNode === parent ? nextSibling : null;
    if (element.parentNode !== parent || element.nextSibling !== anchor) {
        parent.insertBefore(element, anchor);
    }
};

// Keep the existing serialized checklist format after changing its layout.
export const normalizeForm = (form: IPropsElement[][]): IPropsElement[][] => {
    let index = 0;
    return form.filter(row => row.length > 0).map(row => row.map((element, subIndex) => ({
        ...element, index: index++, subIndex
    })));
};

export const moveElement = (
    form: IPropsElement[][],
    source: ElementPosition | undefined,
    target: ElementPosition,
    newElement?: IPropsElement
): IPropsElement[][] => {
    if (target.rowIndex < 0 || target.rowIndex > form.length) return form;
    const next = form.map(row => [...row]);
    const element = source ? next[source.rowIndex]?.[source.position] : newElement;
    if (!element) return form;

    let position = target.position;
    if (source) {
        next[source.rowIndex].splice(source.position, 1);
        if (source.rowIndex === target.rowIndex && source.position < position) position--;
    }
    const row = next[target.rowIndex] || [];
    row.splice(Math.max(0, Math.min(position, row.length)), 0, element);
    next[target.rowIndex] = row;
    return normalizeForm(next);
};

export const insertElementRow = (
    form: IPropsElement[][],
    source: ElementPosition | undefined,
    rowIndex: number,
    newElement?: IPropsElement
): IPropsElement[][] => {
    if (rowIndex < 0 || rowIndex > form.length) return form;
    const next = form.map(row => [...row]);
    const element = source ? next[source.rowIndex]?.[source.position] : newElement;
    if (!element) return form;
    if (source) {
        next[source.rowIndex].splice(source.position, 1);
        if (!next[source.rowIndex].length) {
            next.splice(source.rowIndex, 1);
            if (source.rowIndex < rowIndex) rowIndex--;
        }
    }
    next.splice(rowIndex, 0, [element]);
    return normalizeForm(next);
};

export const moveRow = (form: IPropsElement[][], source: number, target: number): IPropsElement[][] => {
    if (source === target || !form[source] || !form[target]) return form;
    const next = [...form];
    const [row] = next.splice(source, 1);
    next.splice(target, 0, row);
    return normalizeForm(next);
};
